/**
 * Semantic tone vocabulary for status presets.
 *
 * Status presets in `domain/status/*` declare a tone per state. Each design
 * system (`stride`, `nexus`) maps tone → its own visual treatment. The domain
 * layer never makes rendering decisions; it only declares meaning.
 */
export type Tone =
  | 'neutral'
  | 'info'
  | 'progress'
  | 'success'
  | 'warning'
  | 'critical'
  | 'muted'

/**
 * A status preset entry. `label` is the human-readable display string.
 * `tone` carries semantic meaning. `icon` is an optional Lucide icon — passed
 * as the imported component reference (not a string), so consumers don't need
 * a string→component registry.
 */
import type { LucideIcon } from 'lucide-react'

export interface StatusPreset {
  label: string
  tone: Tone
  icon?: LucideIcon
  /**
   * Optional short letter-code (e.g. the canonical event-taxonomy code 'FSR'),
   * rendered as a mono chip prefixed to the label on ops surfaces.
   */
  code?: string
  /** Optional one-line description, surfaced as a hover tooltip on the tag. */
  description?: string
}

/**
 * A keyed status preset map. Looking up an unknown key falls back to a
 * `neutral` preset with the raw key humanized.
 */
export type StatusPresetMap<K extends string> = Record<K, StatusPreset>

/**
 * Resolve a preset by key, with a humanized fallback for unknown keys.
 * Pass the raw value (e.g. database enum) and the preset map.
 */
export function resolveStatus<K extends string>(
  map: StatusPresetMap<K>,
  key: string,
): StatusPreset {
  return (
    (map as Record<string, StatusPreset>)[key] ?? {
      label: humanize(key),
      tone: 'neutral',
    }
  )
}

const labelOverrides: Record<string, string> = {
  todo: 'To Do',
}

/**
 * snake_case → Title Case, with overrides for cases the algorithm gets wrong.
 * Exported so renderers can humanize free-form strings consistently.
 */
export function humanize(value: string): string {
  return (
    labelOverrides[value] ??
    value.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  )
}
